import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '../stores/appStore'
import { formatProbability, getAgentIcon } from '../utils/format'
import clsx from 'clsx'
import { Link } from 'react-router-dom'

/**
 * TaskPanel - A minimizable floating panel for tracking prediction runs.
 *
 * Features:
 * - Minimized: Small pill in bottom-right showing progress
 * - Expanded: Full details with agent stages and results
 * - Persists across navigation (rendered at app root level)
 * - Recovery: On app load, checks for active runs and polls status
 */
export function TaskPanel() {
  const {
    runner,
    closeRunner,
    minimizeRunner,
    maximizeRunner,
  } = useAppStore()

  // Don't render if not open
  if (!runner.isOpen) return null

  const overallProgress = runner.stages.reduce((sum, s) => sum + s.progress, 0) / runner.stages.length

  // Minimized view - small floating pill
  if (runner.isMinimized) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.9 }}
        className="fixed bottom-4 right-4 z-50"
      >
        <button
          onClick={maximizeRunner}
          className={clsx(
            'flex items-center gap-3 px-4 py-3 rounded-full shadow-lg',
            'bg-void-100 border border-star-dim/20',
            'hover:border-signal-blue/50 transition-all',
            'cursor-pointer'
          )}
        >
          {/* Spinning indicator when running */}
          {runner.isRunning && (
            <div className="relative w-5 h-5">
              <div className="absolute inset-0 border-2 border-signal-blue/30 rounded-full" />
              <div className="absolute inset-0 border-2 border-signal-blue border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Checkmark when complete */}
          {!runner.isRunning && runner.result && (
            <div className="w-5 h-5 bg-signal-green/20 rounded-full flex items-center justify-center">
              <span className="text-signal-green text-xs">✓</span>
            </div>
          )}

          {/* Error indicator */}
          {!runner.isRunning && runner.error && (
            <div className="w-5 h-5 bg-signal-red/20 rounded-full flex items-center justify-center">
              <span className="text-signal-red text-xs">✕</span>
            </div>
          )}

          <div className="flex flex-col items-start">
            <span className="text-xs font-mono text-star-muted">
              {runner.isRunning ? 'Running prediction...' : runner.result ? 'Prediction complete' : 'Run failed'}
            </span>
            {runner.isRunning && (
              <span className="text-xs font-mono text-signal-blue">{Math.round(overallProgress)}%</span>
            )}
            {runner.result && (
              <span className="text-xs font-mono text-signal-green">
                {formatProbability(runner.result.final_prob)}
              </span>
            )}
          </div>

          {/* Progress bar */}
          {runner.isRunning && (
            <div className="w-16 h-1.5 bg-void-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-signal-blue rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
              />
            </div>
          )}

          <span className="text-star-dim text-xs">↑</span>
        </button>
      </motion.div>
    )
  }

  // Expanded view - full panel (similar to original modal but as a floating panel)
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-void/80 backdrop-blur-sm p-4"
        onClick={runner.isRunning ? minimizeRunner : closeRunner}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="glass-card p-6 w-full max-w-lg relative overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Scan line effect when running */}
          {runner.isRunning && (
            <div className="scan-line" />
          )}

          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg font-semibold text-star mb-1">
                {runner.isRunning ? 'Running Prediction' : runner.error ? 'Run Failed' : 'Run Complete'}
              </h3>
              <p className="text-sm text-star-muted line-clamp-2">
                {runner.questionTitle}
              </p>
              {runner.runId && (
                <span className="text-xs text-star-dim font-mono">Run #{runner.runId}</span>
              )}
            </div>

            <div className="flex items-center gap-2 ml-4">
              {/* Minimize button (only when running) */}
              {runner.isRunning && (
                <button
                  className="text-star-muted hover:text-star transition-colors p-1"
                  onClick={minimizeRunner}
                  title="Minimize"
                >
                  <span className="text-lg">−</span>
                </button>
              )}
              {/* Close button (only when not running) */}
              {!runner.isRunning && (
                <button
                  className="text-star-muted hover:text-star transition-colors p-1"
                  onClick={closeRunner}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Pipeline visualization */}
          <div className="space-y-3 mb-6">
            {runner.stages.map((stage, index) => (
              <motion.div
                key={stage.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={clsx(
                  stage.status === 'pending' && 'pipeline-step-pending',
                  stage.status === 'running' && 'pipeline-step-running',
                  stage.status === 'complete' && 'pipeline-step-complete',
                  stage.status === 'error' && 'pipeline-step-error'
                )}
              >
                {/* Agent icon */}
                <div className="relative flex-shrink-0">
                  <div className={clsx(
                    'w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all',
                    stage.status === 'pending' && 'bg-void-100',
                    stage.status === 'running' && 'bg-signal-blue/20',
                    stage.status === 'complete' && 'bg-signal-green/20',
                    stage.status === 'error' && 'bg-signal-red/20'
                  )}>
                    {stage.status === 'running' && (
                      <div className="absolute inset-0 pulse-ring" />
                    )}
                    <span>{getAgentIcon(stage.name)}</span>
                  </div>
                </div>

                {/* Stage info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-sm text-star capitalize">
                      {stage.name}
                    </span>
                    <div className="flex items-center gap-2">
                      {/* Direction indicator when complete */}
                      {stage.status === 'complete' && stage.direction && (
                        <span className={clsx(
                          'text-xs font-mono px-1.5 py-0.5 rounded',
                          stage.direction === 'bullish' && 'bg-signal-green/20 text-signal-green',
                          stage.direction === 'bearish' && 'bg-signal-red/20 text-signal-red',
                          stage.direction === 'neutral' && 'bg-star-dim/20 text-star-muted'
                        )}>
                          {stage.direction === 'bullish' ? '↑' : stage.direction === 'bearish' ? '↓' : '→'}
                        </span>
                      )}
                      {/* Probability when complete */}
                      {stage.status === 'complete' && stage.prob !== undefined && (
                        <span className="text-xs font-mono text-signal-blue">
                          {(stage.prob * 100).toFixed(0)}%
                        </span>
                      )}
                      <span className={clsx(
                        'text-xs font-mono',
                        stage.status === 'pending' && 'text-star-dim',
                        stage.status === 'running' && 'text-signal-blue',
                        stage.status === 'complete' && 'text-signal-green',
                        stage.status === 'error' && 'text-signal-red'
                      )}>
                        {stage.status === 'pending' && 'Waiting'}
                        {stage.status === 'running' && 'Processing...'}
                        {stage.status === 'complete' && 'Complete'}
                        {stage.status === 'error' && 'Failed'}
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="h-1 bg-void-200 rounded-full overflow-hidden">
                    <motion.div
                      className={clsx(
                        'h-full rounded-full',
                        stage.status === 'running' && 'bg-signal-blue',
                        stage.status === 'complete' && 'bg-signal-green',
                        stage.status === 'error' && 'bg-signal-red'
                      )}
                      initial={{ width: 0 }}
                      animate={{ width: `${stage.progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>

                  {/* Current search query when running */}
                  {stage.status === 'running' && stage.currentSearch && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-1.5"
                    >
                      <div className="flex items-center gap-1.5 text-xs text-star-dim">
                        <span className="animate-pulse">🔍</span>
                        <span className="truncate">{stage.currentSearch}</span>
                      </div>
                    </motion.div>
                  )}

                  {/* Key findings when complete */}
                  {stage.status === 'complete' && stage.keyFindings && stage.keyFindings.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-2 space-y-1"
                    >
                      {stage.keyFindings.slice(0, 2).map((finding, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs text-star-muted">
                          <span className="text-signal-blue flex-shrink-0">→</span>
                          <span className="line-clamp-1">{finding}</span>
                        </div>
                      ))}
                      {stage.evidenceCount && stage.evidenceCount > 0 && (
                        <div className="text-xs text-star-dim mt-1">
                          {stage.evidenceCount} source{stage.evidenceCount !== 1 ? 's' : ''} cited
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Live Search Activity Feed */}
          {runner.isRunning && runner.searchActivity.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-4 p-3 bg-void-100 rounded-lg border border-star-dim/10"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-star-muted">Search Activity</span>
                <span className="text-xs text-star-dim">({runner.searchActivity.length})</span>
              </div>
              <div className="space-y-1.5 max-h-24 overflow-y-auto">
                {runner.searchActivity.slice(0, 5).map((activity, i) => (
                  <motion.div
                    key={`${activity.query}-${activity.timestamp}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1 - (i * 0.15), x: 0 }}
                    className="flex items-center gap-2 text-xs"
                  >
                    {activity.status === 'started' ? (
                      <span className="animate-pulse text-signal-blue">🔍</span>
                    ) : (
                      <span className="text-signal-green">✓</span>
                    )}
                    <span className="text-star-dim truncate flex-1">{activity.query}</span>
                    {activity.status === 'complete' && activity.resultCount !== undefined && (
                      <span className="text-star-dim text-xs">{activity.resultCount} results</span>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Overall progress */}
          {runner.isRunning && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-star-muted font-mono">Overall Progress</span>
                <span className="text-xs text-star font-mono">{Math.round(overallProgress)}%</span>
              </div>
              <div className="h-2 bg-void-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-signal-blue to-signal-green rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${overallProgress}%` }}
                />
              </div>
              <p className="text-xs text-star-dim mt-2">
                You can minimize this and continue browsing. The prediction will complete in the background.
              </p>
            </div>
          )}

          {/* Result */}
          {runner.result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-signal-green/10 border border-signal-green/30 rounded-lg"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-sm text-signal-green">✓ Prediction Complete</span>
                <span className="text-xs text-star-muted font-mono">
                  Run #{runner.result.run_id}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <span className="data-label">Probability</span>
                  <div className="data-value text-xl">{formatProbability(runner.result.final_prob)}</div>
                </div>
                <div>
                  <span className="data-label">Confidence</span>
                  <div className="data-value text-xl">
                    {runner.result.confidence !== undefined
                      ? `${(runner.result.confidence * 100).toFixed(0)}%`
                      : '—'}
                  </div>
                </div>
                <div>
                  <span className="data-label">Decision</span>
                  <div className={clsx(
                    'data-value text-xl font-semibold',
                    runner.result.decision === 'buy' && 'text-signal-green',
                    runner.result.decision === 'sell' && 'text-signal-red',
                    (!runner.result.decision || runner.result.decision === 'hold') && 'text-star-muted'
                  )}>
                    {runner.result.decision?.toUpperCase() || 'HOLD'}
                  </div>
                </div>
              </div>

              {/* Trade Result */}
              {runner.result.trade && (
                <div className={clsx(
                  'mt-4 p-3 rounded-lg border',
                  runner.result.trade.executed
                    ? 'bg-signal-green/5 border-signal-green/20'
                    : 'bg-void-50 border-star-dim/20'
                )}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={clsx(
                      'text-sm font-mono',
                      runner.result.trade.executed ? 'text-signal-green' : 'text-star-muted'
                    )}>
                      {runner.result.trade.executed ? 'Paper Trade Executed' : 'No Trade'}
                    </span>
                  </div>

                  {runner.result.trade.executed ? (
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-star-muted">Action:</span>
                        <span className={clsx(
                          'font-mono font-semibold',
                          runner.result.trade.side === 'buy' ? 'text-signal-green' : 'text-signal-red'
                        )}>
                          {runner.result.trade.side?.toUpperCase()} {runner.result.trade.qty?.toFixed(1)} shares
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-star-muted">Price:</span>
                        <span className="font-mono text-star">{(runner.result.trade.price! * 100).toFixed(1)}¢</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-star-muted">Notional:</span>
                        <span className="font-mono text-star">${runner.result.trade.usd_notional?.toFixed(2)}</span>
                      </div>
                      {runner.result.trade.traded_question && (
                        <div className="mt-2 pt-2 border-t border-star-dim/20">
                          <span className="text-xs text-star-dim">{runner.result.trade.traded_question}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-star-dim">{runner.result.trade.reason}</span>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Error */}
          {runner.error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-signal-red/10 border border-signal-red/30 rounded-lg"
            >
              <span className="font-mono text-sm text-signal-red">✕ {runner.error}</span>
            </motion.div>
          )}

          {/* Actions */}
          {!runner.isRunning && (
            <div className="flex justify-end gap-3 mt-6">
              <button className="btn-secondary" onClick={closeRunner}>
                Close
              </button>
              {runner.result && runner.questionId && (
                <Link
                  to={`/question/${runner.questionId}`}
                  className="btn-primary"
                  onClick={closeRunner}
                >
                  View Details
                </Link>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
